/**
 * Block model for a SKILL.md. The panel renders these as React elements rather than HTML, so an
 * agent-authored file can never inject markup into the dashboard.
 */
export interface SkillHeadingBlock {
  kind: 'heading';
  /** 1 to 4, as written. */
  level: number;
  text: string;
  /** The number the author typed, or null. Never invented. */
  number: string | null;
  /** Anchor the outline jumps to. */
  id: string;
}

export interface SkillParagraphBlock {
  kind: 'paragraph';
  text: string;
}

export interface SkillListBlock {
  kind: 'list';
  ordered: boolean;
  items: string[];
}

export interface SkillCodeBlock {
  kind: 'code';
  language: string | null;
  text: string;
}

export interface SkillTableBlock {
  kind: 'table';
  head: string[];
  rows: string[][];
}

export interface SkillQuoteBlock {
  kind: 'quote';
  text: string;
}

export interface SkillRuleBlock {
  kind: 'rule';
}

export type SkillBlock =
  | SkillHeadingBlock
  | SkillParagraphBlock
  | SkillListBlock
  | SkillCodeBlock
  | SkillTableBlock
  | SkillQuoteBlock
  | SkillRuleBlock;

/** One run of inline markup inside a paragraph, list item or cell. */
export interface SkillSegment {
  kind: 'text' | 'code' | 'strong';
  text: string;
}

const FRONT_MATTER = /^---\n[\s\S]*?\n---\n?/;
const FENCE = /^\s*```(.*)$/;
const HEADING = /^(#{1,4})\s+(.*)$/;
const NUMBERED = /^(\d+)\.\s+(.*)$/;
const BULLET = /^\s*[-*]\s+(.*)$/;
const ORDERED = /^\s*\d+\.\s+(.*)$/;
const TABLE_ROW = /^\s*\|.*\|\s*$/;
const TABLE_RULE = /^\s*\|[\s:|-]+\|\s*$/;
const QUOTE = /^\s*>\s?(.*)$/;
const RULE = /^\s*---+\s*$/;
const INLINE = /(`[^`]+`|\*\*[^*]+\*\*)/;

function anchorId(text: string, index: number): string {
  const slug = text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
  return `skill-${index}-${slug}`;
}

function cells(row: string): string[] {
  return row
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((cell) => cell.trim());
}

/** Splits one line of prose into literal, code and bold runs; unmatched markers stay literal. */
export function parseInline(source: string): SkillSegment[] {
  const segments: SkillSegment[] = [];
  for (const part of source.split(INLINE)) {
    if (part.length === 0) {
      continue;
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 1) {
      segments.push({ kind: 'code', text: part.slice(1, -1) });
    } else if (part.startsWith('**') && part.endsWith('**') && part.length > 3) {
      segments.push({ kind: 'strong', text: part.slice(2, -2) });
    } else {
      segments.push({ kind: 'text', text: part });
    }
  }
  return segments.length > 0 ? segments : [{ kind: 'text', text: source }];
}

/** Parses a SKILL.md body into blocks. Front matter is dropped; it labels the panel, not the document. */
export function parseSkillBlocks(source: string): SkillBlock[] {
  const lines = source.replace(FRONT_MATTER, '').split('\n');
  const blocks: SkillBlock[] = [];
  let headingCount = 0;
  let i = 0;

  while (i < lines.length) {
    const line = lines[i] ?? '';

    const fence = line.match(FENCE);
    if (fence) {
      const language = (fence[1] ?? '').trim();
      const buffer: string[] = [];
      i += 1;
      while (i < lines.length && !FENCE.test(lines[i] ?? '')) {
        buffer.push(lines[i] ?? '');
        i += 1;
      }
      i += 1;
      blocks.push({ kind: 'code', language: language.length > 0 ? language : null, text: buffer.join('\n') });
      continue;
    }

    const heading = line.match(HEADING);
    if (heading) {
      const raw = (heading[2] ?? '').trim();
      const numbered = raw.match(NUMBERED);
      const text = numbered?.[2] ?? raw;
      blocks.push({
        kind: 'heading',
        level: (heading[1] ?? '#').length,
        text,
        number: numbered?.[1] ?? null,
        id: anchorId(text, headingCount),
      });
      headingCount += 1;
      i += 1;
      continue;
    }

    if (TABLE_ROW.test(line) && TABLE_RULE.test(lines[i + 1] ?? '')) {
      const head = cells(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && TABLE_ROW.test(lines[i] ?? '')) {
        rows.push(cells(lines[i] ?? ''));
        i += 1;
      }
      blocks.push({ kind: 'table', head, rows });
      continue;
    }

    if (QUOTE.test(line)) {
      const buffer: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i] ?? '')) {
        buffer.push((lines[i] ?? '').match(QUOTE)?.[1] ?? '');
        i += 1;
      }
      blocks.push({ kind: 'quote', text: buffer.join(' ') });
      continue;
    }

    if (RULE.test(line)) {
      blocks.push({ kind: 'rule' });
      i += 1;
      continue;
    }

    const ordered = ORDERED.test(line);
    if (ordered || BULLET.test(line)) {
      const pattern = ordered ? ORDERED : BULLET;
      const items: string[] = [];
      while (i < lines.length) {
        const current = lines[i] ?? '';
        const item = current.match(pattern);
        if (item) {
          items.push(item[1] ?? '');
          i += 1;
          continue;
        }
        // An indented continuation belongs to the item above rather than starting a paragraph.
        if (/^\s+\S/.test(current) && items.length > 0) {
          items[items.length - 1] = `${items[items.length - 1]} ${current.trim()}`;
          i += 1;
          continue;
        }
        break;
      }
      blocks.push({ kind: 'list', ordered, items });
      continue;
    }

    if (line.trim().length === 0) {
      i += 1;
      continue;
    }

    const buffer: string[] = [];
    while (i < lines.length) {
      const current = lines[i] ?? '';
      if (current.trim().length === 0) {
        break;
      }
      if (HEADING.test(current) || FENCE.test(current) || BULLET.test(current) || ORDERED.test(current)) {
        break;
      }
      if (QUOTE.test(current) || TABLE_ROW.test(current) || RULE.test(current)) {
        break;
      }
      buffer.push(current.trim());
      i += 1;
    }
    blocks.push({ kind: 'paragraph', text: buffer.join(' ') });
  }

  return blocks;
}
